import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import AddSupplyModal from './AddSupplyModal';
import EditSupplyModal from './EditSupplyModal';
import ViewSupplyModal from './ViewSupplyModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import EditStockModal from './EditStockModal';
import StockHistoryModal from '../StockHistory/StockHistoryModal';
import { REFERENCE_TYPE } from '../../services/StockEventService';
import { getSupplies, createSupply, updateSupply, deleteSupply, updateStock } from '../../services/SuppliesService';
import { getUnitById, permissionOptions } from '../../utils/Enums';
import IOSSwitch from '../Switch/IOSSwitch';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';
import { getProfile } from '../../services/UserService';

import { formatMoney } from '../../utils/currency';
function SuppliesTable({ locationId, isBranch }) {
  const { t } = useTranslation();

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const role = localStorage.getItem('role');
  const [isFranchiseManager, setIsFranchiseManager] = useState(
    role === 'Franquiciante' || role === 'FRANCHISE_MANAGER'
  );
  const [profileLoaded, setProfileLoaded] = useState(false);
  const canManageLocationVisibility = profileLoaded && !isFranchiseManager && hasPermission(permissionOptions.find(p => p.label === 'VIEW_BUSINESS_UNITS').id);
  const canCreateSupplies = profileLoaded && !isFranchiseManager && hasPermission(permissionOptions.find(p => p.label === 'CREATE_SUPPLIES').id);
  const canViewSupplyDetails = hasPermission(permissionOptions.find(p => p.label === 'VIEW_SUPPLIES').id);
  const canEditSupplyDefinition = profileLoaded && !isFranchiseManager && !isBranch && hasPermission(permissionOptions.find(p => p.label === 'UPDATE_SUPPLIES').id);
  const canEditSupplyStock = hasPermission(permissionOptions.find(p => p.label === 'UPDATE_SUPPLIES').id);
  const canDeleteSupplies = profileLoaded && !isFranchiseManager && hasPermission(permissionOptions.find(p => p.label === 'DELETE_SUPPLIES').id);
  const canViewStockHistory = hasPermission(permissionOptions.find(p => p.label === 'VIEW_STOCK_HISTORY').id);

  const [supplies, setSupplies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [modalState, setModalState] = useState({
    add: false,
    edit: false,
    editStock: false,
    view: false,
    delete: false,
    stockHistory: false
  });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    getProfile()
      .then((profile) => {
        if (!isMounted) return;
        const templateCode = profile?.role?.templateCode;
        const roleName = profile?.role?.name;
        setIsFranchiseManager(templateCode === 'FRANCHISE_MANAGER' || roleName === 'Franquiciante');
      })
      .catch(() => {
        if (!isMounted) return;
        setIsFranchiseManager(role === 'Franquiciante' || role === 'FRANCHISE_MANAGER');
      })
      .finally(() => {
        if (isMounted) setProfileLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, [role]);

  const fetchSupplies = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const data = await getSupplies(locationId, page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });

      if (data && typeof data === 'object') {
        setSupplies(data.supplies || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        setSupplies(data || []);
        setTotalRows(data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching supplies:', error);
      setSupplies([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [locationId, sortModel, filterModel]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchSupplies, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchSupplies(1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    }, 500);
  };

  const handleAddSupply = async (supply) => {
    try {
      await createSupply(supply, locationId);
      // Refetch data after adding
      fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
      handleModal('add', false);
    } catch (error) {
      console.error('Error adding supply:', error);
    }
  };

  const handleEditSupply = async (supply) => {
    try {
      await updateSupply(supply, locationId);
      // Refetch data after editing
      fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
      handleModal('edit', false);
    } catch (error) {
      console.error('Error updating supply:', error);
    }
  };

  const handleEditStockSupply = async (supply) => {
    try {
      // Refetch data after editing stock
      fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
      handleModal('editStock', false);
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const handleDeleteSupply = async () => {
    try {
      await deleteSupply(selectedSupply.id);
      // Refetch data after deleting
      fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
      handleModal('delete', false);
    } catch (error) {
      console.error('Error deleting supply:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // El filtrado ahora se hace en el servidor, no necesitamos filteredSupplies
  // Pero mantenemos el filtro de permisos localmente
  const handleModal = (type, open, supply = null) => {
    setModalState({ ...modalState, [type]: open });
    setSelectedSupply(supply);
  };

  const handleToggleVisibility = useCallback(async (supply, checked) => {
    try {
      const response = await updateStock(supply.id, {
        location_id: parseInt(locationId, 10),
        stock: supply.countsStock ? (supply.stock ?? 0) : 0,
        minimum_stock: supply.countsStock ? (supply.minimumStock ?? 0) : 0,
        suggested_stock: supply.countsStock ? (supply.suggestedStock ?? 0) : 0,
        visible_in_location: checked,
      });
      setSupplies((prev) => prev.map((item) => (item.id === supply.id ? response : item)));
    } catch (error) {
      console.error('Error updating supply visibility:', error);
    }
  }, [locationId]);

  const columns = [
    { field: 'name', headerName: t('name'), flex: 2 },
    {
      field: 'stock',
      headerName: t('stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        if (!row.countsStock) return '-';
        return (row.stock ?? 0).toLocaleString('de-DE');
      }
    },
    {
      field: 'minimumStock',
      headerName: t('minimum_stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        if (!row.countsStock) return '-';
        return (row.minimumStock ?? 0).toLocaleString('de-DE');
      }
    },
    {
      field: 'suggestedStock',
      headerName: t('suggested_stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        if (!row.countsStock) return '-';
        return (row.suggestedStock ?? 0).toLocaleString('de-DE');
      }
    },
    ...(canManageLocationVisibility ? [{
      field: 'visibleInLocation',
      headerName: t('visible_in_location'),
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IOSSwitch
            checked={!!params.row.visibleInLocation}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => handleToggleVisibility(params.row, event.target.checked)}
          />
        </Box>
      )
    }] : []),
    { 
      field: 'unit', 
      headerName: t('unit'), 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center', 
      valueGetter: (params) => getUnitById(params)?.label
    },
    { 
      field: 'price', 
      headerName: t('cost'), 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params, row) => formatMoney(params || 0, row?.costCurrency)
    },
    { 
      field: 'primarySupplier', 
      headerName: t('primary_supplier'), 
      flex: 1.5, 
      align: 'center', 
      headerAlign: 'center',
      valueGetter: (params) => params.name || 'N/A'
    }
  ];

  columns.push(
    { 
      field: 'actions', 
      headerName: t('actions'), 
      flex: 2,
      minWidth: 280,
      sortable: false, 
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          {canViewSupplyDetails && (
            <IconButton size="large" onClick={() => handleModal('view', true, params.row)}>
              {getIcon('eye')}
            </IconButton>
          )}
          {canEditSupplyDefinition && (
            <IconButton size="large" onClick={() => handleModal('edit', true, params.row)}>
              {getIcon('settings')}
            </IconButton>
          )}
          {canEditSupplyStock && (
            <IconButton size="large" onClick={() => handleModal('editStock', true, params.row)}>
              {getIcon('bagHandle')}
            </IconButton>
          )}
          {canViewStockHistory && params.row.countsStock !== false && (
            <IconButton size="large" onClick={() => handleModal('stockHistory', true, params.row)}>
              {getIcon('time')}
            </IconButton>
          )}
          {canDeleteSupplies && (
            <IconButton size="large" onClick={() => handleModal('delete', true, params.row)}>
              {getIcon('trash')}
            </IconButton>
          )}
        </>
      )
    }
  )

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_supply_name')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: '300px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchSupplies(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
            />
            {canCreateSupplies && (
              <Button variant="outlined" startIcon={getIcon('add')} onClick={() => handleModal('add', true)}>
                {t('add_supply')}
              </Button>
            )}
          </Box>
        </Box>
        
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1250 }}>
            <DataGrid
              loading={loading}
              rows={supplies}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
              pageSizeOptions={[10, 25, 50]}
              paginationMode="server"
              rowCount={totalRows}
              autoHeight
              getRowClassName={(params) => {
                const { stock, minimumStock, suggestedStock } = params.row;
                if (!params.row.countsStock) return '';
                
                if (stock < minimumStock) {
                  return 'row-stock-red';
                }
                
                if (stock < suggestedStock) {
                  return 'row-stock-yellow';
                }
                
                return '';
              }}
              sx={{
                '& .row-stock-red': { backgroundColor: '#ffebee !important' },
                '& .row-stock-yellow': { backgroundColor: '#fff9e6 !important' },
                '& .MuiDataGrid-row:hover': { opacity: 0.9 },
              }}
            />
          </Box>
        </Box>
        
      </Paper>
      
      {/* Modales */}
      {modalState.add && <AddSupplyModal open={modalState.add} onClose={() => handleModal('add', false)} onSave={handleAddSupply}/>}
      {modalState.edit && <EditSupplyModal open={modalState.edit} supply={selectedSupply} onClose={() => handleModal('edit', false)} onSave={handleEditSupply}/>}
      {modalState.editStock && <EditStockModal locationId={locationId} supply={selectedSupply} open={modalState.editStock} onClose={() => handleModal('editStock', false)} onSave={handleEditStockSupply} showVisibilityToggle={canManageLocationVisibility} />}
      {modalState.view && <ViewSupplyModal open={modalState.view} supply={selectedSupply} onClose={() => handleModal('view', false)} />}
      {modalState.delete && <DeleteConfirmModal open={modalState.delete} onClose={() => handleModal('delete', false)} supplyName={selectedSupply?.name} onDelete={handleDeleteSupply}/>}
      {modalState.stockHistory && (
        <StockHistoryModal
          open={modalState.stockHistory}
          onClose={() => handleModal('stockHistory', false)}
          locationId={locationId}
          referenceType={REFERENCE_TYPE.SUPPLY}
          referenceId={selectedSupply?.id}
          itemName={selectedSupply?.name}
        />
      )}
    </>
  );

}

export default SuppliesTable;
